import React from 'react';
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Spinner
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Check } from 'react-feather';
import { inviteAdminUser } from '../ReduxToolkit/Reducers/auth';
import { permissionsList } from '../Utils';

const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Name is required'),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .trim()
    .min(7, 'Password must be at least 7 characters')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character'
    )
    .required('Password is required'),
  role: yup.string().trim().optional(),
  permissions: yup.object().optional()
});

function InviteUsers() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.auth);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: '',
      permissions: permissionsList.reduce((acc, perm) => {
        acc[perm.id] = false;
        return acc;
      }, {})
    }
  });

  const watchedPermissions = watch('permissions');

  const togglePermission = (permId) => {
    const currentValue = watchedPermissions[permId];
    setValue(`permissions.${permId}`, !currentValue);
  };

  const toggleAllPermissions = (enable) => {
    const newPermissions = permissionsList.reduce((acc, perm) => {
      acc[perm.id] = enable;
      return acc;
    }, {});
    setValue('permissions', newPermissions);
  };

  const onSubmit = async (data) => {
    try {
      // Build permissions object with { [id]: { view: true/false } } structure
      const permissions = permissionsList.reduce((acc, perm) => {
        acc[perm.id] = { view: data.permissions[perm.id] || false };
        return acc;
      }, {});

      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        role: data.role || null,
        permissions
      };

      console.log('Invite User Payload:', payload);

      const result = await dispatch(inviteAdminUser(payload));
      if (result?.statusCode === 200) {
        reset();
        navigate('/admin-users');
      }
    } catch (error) {
      console.error('Error inviting user:', error);
    }
  };

  const enabledCount = Object.values(watchedPermissions || {}).filter(Boolean).length;

  return (
    <div className="page-body">
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <div className="mb-4">
                  <h4
                    className="card-title mb-1"
                    style={{ fontWeight: '600', fontSize: '20px' }}
                  >
                    Invite Admin User
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    Create a new admin user with specific permissions
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <Row className="g-3">
                    {/* Name */}
                    <Col md={6}>
                      <FormGroup className="mb-0">
                        <Label
                          className="form-label"
                          style={{ fontWeight: '500', fontSize: '14px' }}
                        >
                          Name <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="text"
                              placeholder="e.g John Doe"
                              invalid={!!errors.name}
                              style={{ fontSize: '14px' }}
                            />
                          )}
                        />
                        {errors.name && (
                          <FormFeedback>{errors.name.message}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Email */}
                    <Col md={6}>
                      <FormGroup className="mb-0">
                        <Label
                          className="form-label"
                          style={{ fontWeight: '500', fontSize: '14px' }}
                        >
                          Email <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="email"
                              placeholder="e.g john@company.com"
                              invalid={!!errors.email}
                              style={{ fontSize: '14px' }}
                            />
                          )}
                        />
                        {errors.email && (
                          <FormFeedback>{errors.email.message}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Password */}
                    <Col md={6}>
                      <FormGroup className="mb-0">
                        <Label
                          className="form-label"
                          style={{ fontWeight: '500', fontSize: '14px' }}
                        >
                          Password <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="password"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="password"
                              placeholder="Min 7 chars with special character"
                              invalid={!!errors.password}
                              style={{ fontSize: '14px' }}
                            />
                          )}
                        />
                        {errors.password && (
                          <FormFeedback>{errors.password.message}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Role */}
                    <Col md={6}>
                      <FormGroup className="mb-0">
                        <Label
                          className="form-label"
                          style={{ fontWeight: '500', fontSize: '14px' }}
                        >
                          Role
                        </Label>
                        <Controller
                          name="role"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="text"
                              placeholder="e.g Admin, Moderator"
                              invalid={!!errors.role}
                              style={{ fontSize: '14px' }}
                            />
                          )}
                        />
                        {errors.role && (
                          <FormFeedback>{errors.role.message}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Permissions */}
                    <Col md={12}>
                      <FormGroup className="mb-0">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <Label
                            className="form-label mb-0"
                            style={{ fontWeight: '500', fontSize: '14px' }}
                          >
                            Permissions ({enabledCount}/{permissionsList.length} enabled)
                          </Label>
                          <div className="d-flex gap-2">
                            <Button
                              type="button"
                              color="primary"
                              size="sm"
                              onClick={() => toggleAllPermissions(true)}
                              style={{ fontSize: '12px' }}
                            >
                              Enable All
                            </Button>
                            <Button
                              type="button"
                              color="primary"
                              size="sm"
                              onClick={() => toggleAllPermissions(false)}
                              style={{ fontSize: '12px' }}
                            >
                              Disable All
                            </Button>
                          </div>
                        </div>
                        <Row className="g-3">
                          {permissionsList.map((perm) => {
                            const isEnabled = watchedPermissions?.[perm.id] || false;
                            return (
                              <Col key={perm.id} md={4} lg={3}>
                                <div
                                  onClick={() => togglePermission(perm.id)}
                                  style={{
                                    border: isEnabled
                                      ? '2px solid #0d6efd'
                                      : '1px solid #dee2e6',
                                    borderRadius: '8px',
                                    padding: '14px',
                                    cursor: 'pointer',
                                    backgroundColor: isEnabled ? '#f0f7ff' : '#fff',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    minHeight: '70px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                  }}
                                >
                                  {isEnabled && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: '6px',
                                        right: '6px',
                                        backgroundColor: '#0d6efd',
                                        borderRadius: '50%',
                                        width: '18px',
                                        height: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Check size={10} color="#fff" />
                                    </div>
                                  )}

                                  <span
                                    style={{
                                      fontWeight: '500',
                                      fontSize: '16px',
                                      color: isEnabled ? '#0d6efd' : '#333'
                                    }}
                                  >
                                    {perm.label}
                                  </span>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="mt-4 pt-2 border-top">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={isSubmitting || isLoading}
                      size="lg"
                      style={{ minWidth: '200px', fontWeight: '500' }}
                    >
                      {(isSubmitting || isLoading) && <Spinner size="sm" className="me-2" />}
                      {(isSubmitting || isLoading) ? 'Inviting User...' : 'Invite User'}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default InviteUsers;
